// This is my second-pass stab at improving on AnimatedModel.
import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  Color,
  DoubleSide,
  Euler,
  Group,
  type IUniform,
  LoopRepeat,
  Material,
  Mesh,
  MeshStandardMaterial,
  Quaternion,
  ShaderLib,
  ShaderMaterial,
  Skeleton,
  Texture,
  UniformsUtils,
  Vector3
} from "three";
import { clone as cloneSkeleton } from "three/addons/utils/SkeletonUtils.js";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";

import {
  Leg,
  PieceFacts,
  PieceId,
  PieceName,
  Square,
  VariantName
} from "src/api";
import { Scheduler } from "src/shared/Scheduler";
import { createTypedEventEmitter } from "src/shared/util";

import { SequencedMoveAction } from "../../game/board/apis/MoveSequence";
import { squareToWorld } from "../../game/board/utilities/boardCoordinates";
import { BoardPiecesInstanceAPI } from "../../game/board/worldObjects/pieces/BoardPiecesInstanceAPI";
import { LoadableGLTFResource } from "../../loader/LoadableGLTFResource";
import {
  LoadableProtospriteResource,
  ProtoSpriteSheetThreeWithTypes
} from "../../loader/LoadableProtospriteResource";
import {
  BoardInteractionTarget,
  isSameInteractionTarget,
  kCentralInteractions,
  keyForPiece
} from "../../singletons/CentralInteraction";
import { UIIconAnimations, UIIconLayers } from "../../ui/icons/iconTypes";
import { Tween } from "../animation/Tween";
import {
  CLIP_FADE_SECONDS,
  DEFAULT_OUTLINE_COLOR,
  DEFAULT_OUTLINE_EDGE_STRENGTH,
  MOVE_DURATION_SECONDS_PER_UNIT
} from "../renderConstants";
import { disposeObject } from "../utilities/disposeObject";
import { resolveFacingQuaternionAfterMove } from "../utilities/vectorUtils";
import { OutlineSettings } from "../world/OutlineSettings";
import { PickableWorldObject } from "../world/PickableWorldObject";
import {
  WorldObjectAPI,
  WorldObjectDefaultEvents,
  WorldObjectSessionDetails
} from "../world/WorldObject";
import recoloringShaderFrag from "./shaders/recoloringShaderFrag.glsl?raw";
import recoloringShaderVert from "./shaders/recoloringShaderVert.glsl?raw";

// This is for recoloring from the main faction color.
const RECOLOR_TARGET = new Color(0xeeba2b);
const RECOLOR_FALLOFF = 0.1;

// How emissive the lights on our model are.
const EMISSIVE_MAP_TINT = new Color(2, 2, 2);

export type FactionType = "factory";

export type PieceVariantKey = string;

export function getPieceVariantKey(
  pieceName: PieceName,
  variantName: VariantName | null
): PieceVariantKey {
  if (!variantName || variantName === "plain") return pieceName;
  return `${pieceName}:${variantName}`;
}

export function getPartsOfPieceVariantKey(
  key: string
): [PieceName | null, VariantName | null] {
  const parts = key.split(":");
  if (parts.length === 0) {
    return [null, null];
  }
  if (parts.length === 1) {
    return [parts[0] as PieceName, null];
  }
  return [parts[0] as PieceName, parts[1] as VariantName];
}

export type GameModelMetadata = {
  scale?: number;
  idleAnimationName?: string;
  moveAnimationName?: string;
  attackAnimationName?: string;
  attackedAnimationName?: string;
  deathAnimationName?: string;
};

export class FactionModels {
  public readonly events = createTypedEventEmitter<Record<string, never>>();
  public readonly factionType: FactionType = "factory";
  public readonly primaryColor: Color;

  private pieceVariantResources = new Map<
    PieceVariantKey,
    LoadableGLTFResource
  >();
  private iconResource:
    LoadableProtospriteResource<UIIconLayers, UIIconAnimations> | undefined;
  private pieceVariantMetadata = new Map<PieceVariantKey, GameModelMetadata>();
  private instances = new Map<PieceId, FactionModelInstance>();

  constructor(opt: { primaryColor?: Color }) {
    this.primaryColor = opt.primaryColor ?? new Color(1, 1, 1);
  }

  addGLTF(
    gltfResource: LoadableGLTFResource,
    pieceName: PieceName,
    variantName: VariantName | null,
    metadata?: GameModelMetadata
  ) {
    const pieceVariantKey = getPieceVariantKey(pieceName, variantName);
    this.pieceVariantResources.set(pieceVariantKey, gltfResource);
    if (metadata) this.pieceVariantMetadata.set(pieceVariantKey, metadata);
    return this;
  }

  addIcons(
    iconResource: LoadableProtospriteResource<UIIconLayers, UIIconAnimations>
  ) {
    this.iconResource = iconResource;
    return this;
  }

  create(piece: PieceFacts) {
    const pieceVariantKey = getPieceVariantKey(piece.type, piece.variant);
    const resource = this.pieceVariantResources.get(pieceVariantKey);
    if (!resource) {
      console.warn("Broken invariant; no piece available.", piece, this);
      throw new Error("Borked invariant!");
    }
    const metadata = this.pieceVariantMetadata.get(pieceVariantKey);

    // This is a kludge but it works for now.
    let color = new Color();
    switch (piece.side % 3) {
      case 0:
        color = new Color(1.1, 0.5, 0.1);
        break;
      case 1:
        color = new Color(1, 0.2, 0.2);
        break;
      case 2:
        color = new Color(1, 1, 0.1);
        break;
    }

    const instance = new FactionModelInstance({
      factionModels: this,
      gltf: resource.getResource(),
      iconSheet: this.iconResource?.isLoaded()
        ? this.iconResource.getResource()
        : undefined,
      color,
      metadata,
      id: piece.id
    });
    instance.setPieceFacts(piece);
    this.instances.set(instance.id, instance);
    return instance;
  }

  removeInstance(instance: FactionModelInstance) {
    this.instances.delete(instance.id);
  }
}

/**
 * A ShaderMaterial that draws like the GLTF's MeshStandardMaterial, with the
 * recolor swapped in for <map_fragment>. Each texture slot is switched on
 * only when the source has that texture, and reads the UV channel and
 * transform the texture says, the way three's own materials do.
 */
function createRecoloringMaterial(
  source: MeshStandardMaterial,
  color: Color
): ShaderMaterial {
  const defines: Record<string, string | number> = {};
  const uniforms: Record<string, IUniform> = {
    ...UniformsUtils.clone(ShaderLib.standard.uniforms),
    diffuse: { value: source.map ? source.color.clone() : color.clone() },
    opacity: { value: source.opacity },
    emissive: {
      value: source.emissiveMap
        ? EMISSIVE_MAP_TINT.clone()
        : source.emissive.clone()
    },
    roughness: { value: source.roughness },
    metalness: { value: source.metalness },
    recolorTarget: { value: RECOLOR_TARGET.clone() },
    recolorTo: { value: color.clone() },
    recolorFalloff: { value: source.map ? RECOLOR_FALLOFF : -1 }
  };

  const enableTexture = (
    slot: string,
    define: string,
    texture: Texture | null
  ) => {
    if (!texture) return;
    if (texture.matrixAutoUpdate) texture.updateMatrix();
    defines[`USE_${define}`] = 1;
    defines[`${define}_UV`] =
      texture.channel === 0 ? "uv" : `uv${texture.channel}`;
    uniforms[slot] = { value: texture };
    uniforms[`${slot}Transform`] = { value: texture.matrix };
  };
  if (source.map) {
    enableTexture("map", "MAP", source.map);
    enableTexture("emissiveMap", "EMISSIVEMAP", source.emissiveMap);
    // GLTFLoader points both of these at the one metallic-roughness texture.
    enableTexture("roughnessMap", "ROUGHNESSMAP", source.roughnessMap);
    enableTexture("metalnessMap", "METALNESSMAP", source.metalnessMap);
  }

  return new ShaderMaterial({
    fragmentShader: recoloringShaderFrag,
    vertexShader: recoloringShaderVert,
    defines,
    uniforms,
    side: DoubleSide,
    transparent: true,
    lights: true
  });
}

export class FactionModelInstance
  implements WorldObjectAPI, PickableWorldObject
{
  public readonly id: PieceId;
  public readonly events = createTypedEventEmitter<
    WorldObjectDefaultEvents & {
      selected: void; // TODO
      deselected: void; // TODO
      updated: void;
      animationCompleted: void; // TODO
      disposed: void;
    }
  >();
  public readonly root = new Group();
  public readonly color: Color;
  public skeleton: Skeleton | null = null;

  private readonly factionModels: FactionModels;
  private boardInstanceAPI?: BoardPiecesInstanceAPI;
  private outline: OutlineSettings | null = null;
  private clips: AnimationClip[] = [];
  private mixer: AnimationMixer;
  private playingAction: AnimationAction | null = null;
  private idleClip: string | null = null;
  private moveClip: string | null = null;
  private attackClip: string | null = null;
  private damagedClip: string | null = null;
  private deathClip: string | null = null;

  // private directionalIndicator?: ProtoSpriteThree<
  //   UIIconLayers,
  //   UIIconAnimations
  // >;

  // This needs work.
  private moving?: {
    from: Vector3;
    to: Vector3;
    fromQuaternion: Quaternion;
    toQuaternion: Quaternion;
    tween: Tween;
    tweenQuaternion: Tween;
  };
  private dying = false;

  protected scheduler = new Scheduler();
  protected currentSquare: Square | null = null;
  protected pieceFacts?: PieceFacts;
  protected sessionDetails: WorldObjectSessionDetails = {};

  constructor(opt: {
    factionModels: FactionModels;
    gltf: GLTF;
    iconSheet?: ProtoSpriteSheetThreeWithTypes<{
      0: {
        layers: UIIconLayers;
        tags: UIIconAnimations;
      };
    }>;
    color: Color;
    square?: Square;
    metadata?: GameModelMetadata;
    id: PieceId;
  }) {
    this.factionModels = opt.factionModels;
    this.color = opt.color.clone();
    this.id = opt.id;

    const cloned = cloneSkeleton(opt.gltf.scene);
    const materialMap = new Map<Material, Material>();
    cloned.traverse((object) => {
      // Special logic for meshes.
      if (object instanceof Mesh) {
        const cloneMesh = object as Mesh;
        const cloneGeometry = object.geometry.clone();
        cloneMesh.geometry = cloneGeometry;

        // Install a custom shader material for pieces with the right prerequisites.
        let newMat: Material = Array.isArray(cloneMesh.material)
          ? cloneMesh.material[0]
          : cloneMesh.material;

        if (materialMap.has(newMat)) {
          cloneMesh.material = materialMap.get(newMat) ?? newMat;
        } else {
          const oldMat = newMat as MeshStandardMaterial;
          newMat = createRecoloringMaterial(oldMat, this.color);
          materialMap.set(oldMat, newMat);
          cloneMesh.material = newMat;
        }

        cloneMesh.castShadow = true;
        cloneMesh.receiveShadow = true;
      }
    });
    this.root.add(cloned);

    this.clips = opt.gltf.animations;
    this.mixer = new AnimationMixer(this.root);

    if (opt.square) this.moveToSquare(opt.square);

    if (opt.metadata) {
      if (opt.metadata.scale !== undefined) {
        this.root.scale.set(
          opt.metadata.scale,
          opt.metadata.scale,
          opt.metadata.scale
        );
      }
      this.idleClip = opt.metadata.idleAnimationName ?? null;
      this.moveClip = opt.metadata.moveAnimationName ?? null;
      this.attackClip = opt.metadata?.attackAnimationName ?? null;
      this.damagedClip = opt.metadata?.attackedAnimationName ?? null;
      this.deathClip = opt.metadata?.deathAnimationName ?? null;
      if (this.idleClip) this.playClip(this.idleClip);
    }

    // if (opt.iconSheet) {
    //   this.directionalIndicator = opt.iconSheet.getSprite();
    //   this.directionalIndicator.gotoAnimation("arrow_right");
    //   this.directionalIndicator.gotoAnimationFrame(0);
    //   this.directionalIndicator.center();
    //   this.directionalIndicator.mesh.position.z += 1;
    //   this.directionalIndicator.mesh.position.y += 0.05 + Math.random() * 0.05;
    //   this.directionalIndicator.mesh.setRotationFromEuler(
    //     new Euler(Math.PI / 2, 0, Math.PI / 2)
    //   );
    //   this.directionalIndicator.mesh.scale.multiplyScalar(0.04);
    //   this.root.add(this.directionalIndicator.mesh);
    // }
  }

  dispose() {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.mixer.getRoot());
    disposeObject(this.root, false);
    // this.directionalIndicator?.dispose();
    this.factionModels.removeInstance(this);
    this.events.emit("disposed");
  }

  playClipForAction(
    actionName: SequencedMoveAction,
    duration?: number,
    loops?: number
  ) {
    switch (actionName) {
      case "moving":
        if (this.moveClip) {
          this.playClip(this.moveClip, duration, loops);
          return;
        }
        break;
      case "attacking":
        if (this.attackClip) {
          this.playClip(this.attackClip, duration, 1);
          return;
        }
        break;
      case "attacked":
        if (this.damagedClip) {
          this.playClip(this.damagedClip, duration, 1);
          return;
        }
        break;
      case "dying":
        if (this.deathClip) {
          this.playClip(this.deathClip, duration, 1);
          return;
        }
        break;
    }
  }

  playClip(clipName: string | null, duration?: number, loops?: number) {
    this.playingAction?.fadeOut(CLIP_FADE_SECONDS);
    const clip = clipName
      ? AnimationClip.findByName(this.clips, clipName)
      : null;
    this.playingAction = clip ? this.mixer.clipAction(clip) : null;
    this.playingAction?.reset().fadeIn(CLIP_FADE_SECONDS).play();
    if (duration !== undefined && duration !== 0) {
      this.playingAction?.setDuration(duration);
    }
    if (loops !== undefined && loops !== 0) {
      this.playingAction?.setLoop(LoopRepeat, loops);
    }
  }

  setPieceFacts(facts: PieceFacts | null) {
    this.pieceFacts = facts ?? undefined;
    return this;
  }

  // Deprecated.
  setPieceInstancesAPI(api?: BoardPiecesInstanceAPI | null) {
    this.boardInstanceAPI = api ?? undefined;
    return this;
  }

  get square(): Square | null {
    return this.currentSquare;
  }

  update(deltaSeconds: number) {
    this.mixer.update(deltaSeconds);
    this.scheduler.step(deltaSeconds);
    if (this.moving) {
      this.moving.tween.step(deltaSeconds);
      this.moving.tweenQuaternion.step(deltaSeconds);
      if (this.moving.tween.isFinished) {
        this.root.position.copy(this.moving.to);
        if (this.moveClip) this.playClip(this.idleClip);
      } else {
        const pos = this.moving.from
          .clone()
          .lerp(this.moving.to, this.moving.tween.progress);
        this.root.position.copy(pos);
      }
      if (this.moving.tweenQuaternion.isFinished) {
        this.root.quaternion.copy(this.moving.toQuaternion);
      } else {
        const quat = this.moving.fromQuaternion
          .clone()
          .slerp(
            this.moving.toQuaternion,
            this.moving.tweenQuaternion.progress
          );
        this.root.quaternion.copy(quat);
      }
      if (
        this.moving.tween.isFinished &&
        this.moving.tweenQuaternion.isFinished
      ) {
        this.moving = undefined;
      }
    }
    if (this.dying && !this.playingAction?.isRunning()) {
      this.root.visible = false;
      this.events.emit("died");
    }
    if (!this.playingAction?.isRunning()) {
      this.playClip(this.idleClip);
    }
    this.events.emit("updated");
  }

  // kludge.
  setOutline(outlined: boolean) {
    this.setSelected(outlined);
  }

  // kludge.
  getOutline() {
    return this.outline;
  }

  getSessionDetails(): WorldObjectSessionDetails {
    return this.sessionDetails;
  }
  setSessionDetails(details: WorldObjectSessionDetails): void {
    this.sessionDetails = details;
    this.computeAndUpdateOutline();
  }

  step(deltaSeconds: number) {
    this.update(deltaSeconds);
  }

  protected setOutlineData(outline: OutlineSettings | null) {
    this.outline = outline;
    this.events.emit("updated");
  }

  protected computeAndUpdateOutline() {
    if (
      !this.sessionDetails.hovered &&
      !this.sessionDetails.selected &&
      !this.sessionDetails.targetedAlly &&
      !this.sessionDetails.targetedEnemy
    ) {
      return this.setOutlineData(null);
    }
    if (this.sessionDetails.selected) {
      return this.setOutlineData({
        color: DEFAULT_OUTLINE_COLOR,
        edgeStrength: DEFAULT_OUTLINE_EDGE_STRENGTH
      });
    } else if (this.sessionDetails.hovered) {
      return this.setOutlineData({
        color: DEFAULT_OUTLINE_COLOR,
        edgeStrength: DEFAULT_OUTLINE_EDGE_STRENGTH * 0.5
      });
    }
    return;
  }

  setSelected(selected: boolean): void {
    this.sessionDetails.selected = selected;
    this.computeAndUpdateOutline();
  }

  setHover(hover: boolean): void {
    this.sessionDetails.hovered = hover;
    this.computeAndUpdateOutline();
  }

  // This method is used by BoardPieces.
  setSquareButDontMove(square: Square) {
    this.currentSquare = square;
    return this;
  }

  // This is used by BoardPieces.
  setMoving(moving: typeof this.moving) {
    this.moving = moving;
  }

  // This is used less than it was.
  moveToSquare(square: Square, animate = false, isAttack = false, leg?: Leg) {
    const otherPiece = isAttack
      ? this.boardInstanceAPI?.getPieceInstanceAt(square)
      : null;
    this.currentSquare = square;
    const targetPos = squareToWorld(square);
    const moveQuaternion = resolveFacingQuaternionAfterMove(
      this.root.position,
      targetPos,
      false
    );
    const targetQuaternion = resolveFacingQuaternionAfterMove(
      this.root.position,
      targetPos
    );

    if (animate) {
      if (!this.moving && this.moveClip) this.playClip(this.moveClip);
      const squaresMoved = targetPos
        .clone()
        .sub(this.root.position)
        .manhattanLength();
      const moveDuration = squaresMoved * MOVE_DURATION_SECONDS_PER_UNIT;
      const moveStartsAt = MOVE_DURATION_SECONDS_PER_UNIT / 2;
      let attackStartsAt = -1;
      let attackPos: Vector3 | null = null;
      let moveBeforeAttack = true;
      if (isAttack) {
        attackStartsAt =
          Math.max(0, moveDuration - MOVE_DURATION_SECONDS_PER_UNIT) +
          moveStartsAt;
        if (attackStartsAt === 0) {
          moveBeforeAttack = false;
        }
        const attackDist = Math.max(
          0,
          targetPos.clone().sub(this.root.position).length() - 1
        );
        attackPos = targetPos
          .clone()
          .sub(this.root.position)
          .normalize()
          .multiplyScalar(attackDist)
          .add(this.root.position);
      }

      this.scheduler.add({
        id: "turn",
        startIn: 0,
        duration: moveStartsAt,
        invokeFunctionAtStart: () => {
          this.moving = {
            from: this.root.position.clone(),
            to: this.root.position.clone(),
            fromQuaternion: this.root.quaternion.clone(),
            toQuaternion: moveQuaternion,
            tween: new Tween(moveStartsAt),
            tweenQuaternion: new Tween(moveStartsAt)
          };
        }
      });

      if (moveBeforeAttack && attackPos !== null) {
        // If we're moving before we attack something, perform a move, then an attack, then a move.
        this.scheduler.add({
          id: "move",
          startIn: moveStartsAt,
          duration: attackStartsAt - moveStartsAt,
          invokeFunctionAtStart: () => {
            if (this.moveClip) this.playClip(this.moveClip, 3);
            this.moving = {
              from: this.root.position.clone(),
              to: attackPos.clone(),
              fromQuaternion: moveQuaternion,
              toQuaternion: moveQuaternion,
              tween: new Tween(attackStartsAt - moveStartsAt),
              tweenQuaternion: new Tween(attackStartsAt - moveStartsAt)
            };
          }
        });
        this.scheduler.add({
          id: "attack",
          startIn: attackStartsAt,
          duration: 1,
          invokeFunctionAtStart: () => {
            if (this.attackClip) this.playClip(this.attackClip, 2);
            if (leg) otherPiece?.notifyAttacked?.(leg);
            this.moving = {
              from: this.root.position.clone(),
              to: attackPos.clone(),
              fromQuaternion: moveQuaternion,
              toQuaternion: moveQuaternion,
              tween: new Tween(1),
              tweenQuaternion: new Tween(1)
            };
          }
        });
        this.scheduler.add({
          id: "move",
          startIn: attackStartsAt + 1,
          duration: 1,
          invokeFunctionAtStart: () => {
            if (this.moveClip) this.playClip(this.moveClip, 1);
            this.moving = {
              from: attackPos.clone(),
              to: targetPos.clone(),
              fromQuaternion: moveQuaternion,
              toQuaternion: targetQuaternion,
              tween: new Tween(1),
              tweenQuaternion: new Tween(1)
            };
          }
        });
      } else {
        this.scheduler.add({
          id: "move",
          startIn: moveStartsAt,
          duration: MOVE_DURATION_SECONDS_PER_UNIT / 2,
          invokeFunctionAtStart: () => {
            this.moving = {
              from: this.root.position.clone(),
              to: targetPos,
              fromQuaternion: moveQuaternion,
              toQuaternion: moveQuaternion,
              tween: new Tween(squaresMoved * MOVE_DURATION_SECONDS_PER_UNIT),
              tweenQuaternion: new Tween(
                squaresMoved * MOVE_DURATION_SECONDS_PER_UNIT
              )
            };
          }
        });
      }

      this.moving = {
        from: this.root.position.clone(),
        to: targetPos,
        fromQuaternion: this.root.quaternion.clone(),
        toQuaternion: targetQuaternion,
        tween: new Tween(squaresMoved * MOVE_DURATION_SECONDS_PER_UNIT),
        tweenQuaternion: new Tween(MOVE_DURATION_SECONDS_PER_UNIT)
      };
    } else {
      if (this.moving && this.moveClip) this.playClip(this.idleClip, 3);
      this.moving = undefined;
      this.root.position.copy(targetPos);
      this.root.quaternion.copy(targetQuaternion);
    }

    return this;
  }

  setDirection(direction: number) {
    const euler = new Euler(0, direction, 0);
    this.root.quaternion.setFromEuler(euler);
    return this;
  }

  // Selection callbacks
  onPicked() {
    // Kludge.
    if (this.currentSquare === null || !this.pieceFacts) return;

    // Submit the interaction to the main singleton.
    const interactionTarget: BoardInteractionTarget = {
      domain: "3d",
      square: this.currentSquare,
      key: keyForPiece(this.currentSquare, this.pieceFacts),
      worldObject: this
    };
    if (
      isSameInteractionTarget(
        interactionTarget,
        kCentralInteractions.getSelection()
      )
    )
      return;
    kCentralInteractions.doClick(interactionTarget);
  }
  onUnpicked() {
    // Also kludge.
  }
  onHover() {
    if (!this.currentSquare || !this.pieceFacts) return;
    const interactionTarget: BoardInteractionTarget = {
      domain: "3d",
      square: this.currentSquare,
      key: keyForPiece(this.currentSquare, this.pieceFacts),
      worldObject: this
    };
    if (
      isSameInteractionTarget(
        interactionTarget,
        kCentralInteractions.getHover()
      )
    )
      return;

    kCentralInteractions.doHover(interactionTarget);
  }
  onUnhover(): void {
    // Also kludge.
  }

  notifyAttacked(leg: Leg): void {
    if (this.damagedClip) this.playClip(this.damagedClip);
  }
  notifyDead(): boolean {
    if (this.deathClip) {
      this.dying = true;
      this.playClip(this.deathClip);
      return true;
    }
    return false;
  }
}
